import { useState, useEffect } from 'react'
import orellaOIcon from '../assets/logos/orella_o_icon.png'
import AskOrellaChip from './AskOrellaChip'
import { DIALOGUE_BUBBLE, DIALOGUE_PAD } from './agent/dialogueBubble'

/**
 * OrellaPageHeader v154.3.1
 * 
 * Animated Orella dialogue header for tab screens (Home, Cases, Visits, Profile).
 * Chips render INSIDE the speech bubble for a cohesive, contained feel.
 * 
 * Props:
 *   message        — The Orella dialogue text
 *   sessionKey     — Unique key for animation re-trigger on content change
 *   chips          — Array of { label, onClick, variant?, icon? }
 *                    variant: 'primary' (solid blue) | 'orella' (ice blue, navy border — default)
 *   onAnimationDone — Callback when animation completes (for downstream content reveal)
 */

function TypingDots() {
  return (
    <div className="flex gap-1.5 py-1.5">
      {[0, 0.15, 0.3].map((delay, i) => (
        <span
          key={i}
          className="w-[6px] h-[6px] rounded-full"
          style={{
            backgroundColor: '#A7D9FC',
            animation: `orellaHeaderDotPulse 1.2s ease-in-out infinite ${delay}s`,
          }}
        />
      ))}
    </div>
  )
}

export default function OrellaPageHeader({ message, sessionKey, chips = [], onAnimationDone, skipAnimation = false }) {
  // v180.1.1: Only animate once per session per screen — use sessionKey as gate
  const alreadyPlayed = sessionKey ? sessionStorage.getItem(`orella_header_${sessionKey}`) : false
  const shouldSkip = skipAnimation || !!alreadyPlayed

  const [showLogo, setShowLogo] = useState(shouldSkip)
  const [showBubble, setShowBubble] = useState(shouldSkip)
  const [showDots, setShowDots] = useState(false)
  const [showText, setShowText] = useState(shouldSkip)
  const [showChips, setShowChips] = useState(shouldSkip)

  useEffect(() => {
    if (shouldSkip) {
      setShowLogo(true); setShowBubble(true); setShowText(true); setShowChips(true)
      onAnimationDone?.()
      return
    }
    const timers = []
    timers.push(setTimeout(() => setShowLogo(true), 200))
    timers.push(setTimeout(() => { setShowBubble(true); setShowDots(true) }, 400))
    timers.push(setTimeout(() => { setShowDots(false); setShowText(true) }, 1200))
    timers.push(setTimeout(() => { 
      setShowChips(true)
      onAnimationDone?.()
      if (sessionKey) sessionStorage.setItem(`orella_header_${sessionKey}`, '1')
    }, 1500))
    return () => timers.forEach(clearTimeout)
  }, [sessionKey])

  return (
    <>
      <style>{`
        @keyframes orellaHeaderDotPulse {
          0%, 80%, 100% { opacity: 0.3; transform: scale(0.8); }
          40% { opacity: 1; transform: scale(1.1); }
        }
        @keyframes orellaHeaderFadeIn {
          from { opacity: 0; transform: translateY(6px); }
          to { opacity: 1; transform: translateY(0); }
        }
      `}</style>
      <div className="px-4 pt-3 pb-2">
        <div className="flex items-start gap-3">
          <img
            src={orellaOIcon}
            alt="Orella"
            className="w-10 h-10 rounded-xl flex-shrink-0 object-contain"
            style={{
              opacity: showLogo ? 1 : 0,
              transform: showLogo ? 'scale(1)' : 'scale(0.75)',
              transition: 'opacity 0.35s ease, transform 0.35s ease',
            }}
          />
          {/* Speech bubble — message + chips all inside */}
          <div
            className={`flex-1 ${DIALOGUE_BUBBLE} ${DIALOGUE_PAD}`}
            style={{
              opacity: showBubble ? 1 : 0,
              transform: showBubble ? 'translateY(0) scale(1)' : 'translateY(4px) scale(0.97)',
              transition: 'opacity 0.35s ease, transform 0.35s ease',
            }}
          >
            {showDots && !showText && <TypingDots />}
            {showText && (
              <p
                className="text-[15.5px] font-medium text-[#15354F] leading-relaxed"
                style={{ animation: 'orellaHeaderFadeIn 0.3s ease' }}
              >
                {message}
              </p>
            )}
            {/* Chips — inside the bubble, below the message */}
            {showChips && chips.length > 0 && (
              <div
                className="flex flex-wrap gap-2 mt-3 pt-3 border-t border-[#A7D9FC]/30"
                style={{ animation: 'orellaHeaderFadeIn 0.3s ease' }}
              >
                {chips.map((chip) => (
                  chip.variant === 'primary' ? (
                    <button
                      key={chip.label}
                      onClick={chip.onClick}
                      className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-[13px] font-semibold text-white active:scale-[0.96] transition-transform breathe-blue"
                      style={{ background: '#46A8F9' }}
                    >
                      {chip.icon && <chip.icon size={14} />}
                      {chip.label}
                    </button>
                  ) : (
                    <AskOrellaChip
                      key={chip.label}
                      onClick={chip.onClick}
                      label={chip.label}
                      variant="outline"
                    />
                  )
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  )
}
